import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface AppSettings {
  tempUnit:   'C' | 'F';
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
  timezone:   string;
  language:   'en' | 'es';
}

const DEFAULTS: AppSettings = {
  tempUnit:   'C',
  dateFormat: 'MM/DD/YYYY',
  timezone:   'America/New_York',
  language:   'en',
};

const KEY = 'app_settings';

function load(): AppSettings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

interface Ctx {
  settings: AppSettings;
  update: (patch: Partial<AppSettings>) => void;
  convertTemp: (celsius: number) => { value: number; unit: string };
  formatTemp: (celsius: number) => string;
}

const SettingsContext = createContext<Ctx | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(load);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(settings));
  }, [settings]);

  const update = (patch: Partial<AppSettings>) =>
    setSettings(prev => ({ ...prev, ...patch }));

  const convertTemp = (celsius: number) => {
    if (settings.tempUnit === 'F') {
      return { value: Math.round((celsius * 9 / 5 + 32) * 10) / 10, unit: '°F' };
    }
    return { value: celsius, unit: '°C' };
  };

  const formatTemp = (celsius: number) => {
    const { value, unit } = convertTemp(celsius);
    return `${value}${unit}`;
  };

  return (
    <SettingsContext.Provider value={{ settings, update, convertTemp, formatTemp }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside SettingsProvider');
  return ctx;
}
